import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import OnboardingTour from './OnboardingTour';
import OnboardingChecklist from './OnboardingChecklist';
import OnboardingLauncher from './OnboardingLauncher';
import { api } from '../../services/api';
import { JWT_TOKEN_KEY } from '../../config/api.config.js';

const OnboardingContext = createContext(null);

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
};

const OnboardingProvider = ({
  children,
  userId,
  userRole,
  currentPage,
  apiBaseUrl = '/api/v1/onboarding',
  enabled = true,
}) => {
  const [tours, setTours] = useState([]);
  const [checklists, setChecklists] = useState([]);
  const [launchers, setLaunchers] = useState([]);
  const [themes, setThemes] = useState([]);
  const [activeTour, setActiveTour] = useState(null);
  const [activeChecklist, setActiveChecklist] = useState(null);
  const [tourProgress, setTourProgress] = useState({});
  const [checklistProgress, setChecklistProgress] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Helper to check if user is authenticated (has token in localStorage)
  const isAuthenticated = useCallback(() => {
    const token = localStorage.getItem(JWT_TOKEN_KEY);
    return Boolean(token);
  }, []);

  const fetchTours = useCallback(async () => {
    if (!enabled || !isAuthenticated()) return;
    
    try {
      const params = new URLSearchParams();
      params.append('status', 'published');
      if (userRole) params.append('role', userRole);
      if (currentPage) params.append('page', currentPage);

      const response = await api.get(`${apiBaseUrl}/tours?${params.toString()}`);
      setTours(response.data || []);
    } catch (err) {
      // Silently handle 401 errors - user is not authenticated yet
      if (err.response?.status !== 401) {
        console.error('Failed to fetch tours:', err);
        setError(err);
      }
    }
  }, [apiBaseUrl, enabled, userRole, currentPage, isAuthenticated]);

  const fetchChecklists = useCallback(async () => {
    if (!enabled || !isAuthenticated()) return;
    
    try {
      const params = new URLSearchParams();
      if (userRole) params.append('role', userRole);

      const response = await api.get(`${apiBaseUrl}/checklists?${params.toString()}`);
      const data = response.data || [];
      setChecklists(data);
      if (data.length > 0) {
        setActiveChecklist(data[0]);
      }
    } catch (err) {
      // Silently handle 401 errors - user is not authenticated yet
      if (err.response?.status !== 401) {
        console.error('Failed to fetch checklists:', err);
      }
    }
  }, [apiBaseUrl, enabled, userRole, isAuthenticated]);

  const fetchLaunchers = useCallback(async () => {
    if (!enabled || !isAuthenticated()) return;
    
    try {
      const params = new URLSearchParams();
      if (userRole) params.append('role', userRole);
      if (currentPage) params.append('page', currentPage);

      const response = await api.get(`${apiBaseUrl}/launchers?${params.toString()}`);
      setLaunchers(response.data || []);
    } catch (err) {
      // Silently handle 401 errors - user is not authenticated yet
      if (err.response?.status !== 401) {
        console.error('Failed to fetch launchers:', err);
      }
    }
  }, [apiBaseUrl, enabled, userRole, currentPage, isAuthenticated]);

  const fetchThemes = useCallback(async () => {
    if (!enabled || !isAuthenticated()) return;
    
    try {
      const response = await api.get(`${apiBaseUrl}/themes`);
      setThemes(response.data || []);
    } catch (err) {
      // Silently handle 401 errors - user is not authenticated yet
      if (err.response?.status !== 401) {
        console.error('Failed to fetch themes:', err);
      }
    }
  }, [apiBaseUrl, enabled, isAuthenticated]);

  const fetchTourProgress = useCallback(async (tourId) => {
    if (!enabled || !userId) return null;
    
    try {
      const response = await api.get(
        `${apiBaseUrl}/progress/tours/${tourId}?user_id=${userId}`
      );
      const data = response.data;
      setTourProgress(prev => ({ ...prev, [tourId]: data }));
      return data;
    } catch (err) {
      console.error('Failed to fetch tour progress:', err);
    }
    return null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBaseUrl, enabled, userId]);

  const startTour = useCallback(async (tourId) => {
    if (!enabled || !userId) return;

    const tour = tours.find(t => t.id === tourId);
    if (!tour) return;

    try {
      const response = await api.post(
        `${apiBaseUrl}/progress/tours/${tourId}/start?user_id=${userId}`
      );
      const progress = response.data;
      setTourProgress(prev => ({ ...prev, [tourId]: progress }));
      setActiveTour(tour);
    } catch (err) {
      console.error('Failed to start tour:', err);
      setActiveTour(tour);
    }
  }, [apiBaseUrl, enabled, userId, tours]);

  const advanceTourStep = useCallback(async (tourId, stepId) => {
    if (!enabled || !userId) return;

    try {
      const response = await api.post(
        `${apiBaseUrl}/progress/tours/${tourId}/advance?user_id=${userId}&step_id=${stepId}`
      );
      const progress = response.data;
      setTourProgress(prev => ({ ...prev, [tourId]: progress }));
    } catch (err) {
      console.error('Failed to advance tour step:', err);
    }
  }, [apiBaseUrl, enabled, userId]);

  const dismissTour = useCallback(async (tourId, reason = 'user_closed') => {
    if (!enabled || !userId) {
      setActiveTour(null);
      return;
    }

    try {
      await api.post(
        `${apiBaseUrl}/progress/tours/${tourId}/dismiss?user_id=${userId}&reason=${reason}`
      );
    } catch (err) {
      console.error('Failed to dismiss tour:', err);
    }
    
    setActiveTour(null);
  }, [apiBaseUrl, enabled, userId]);

  const completeChecklistItem = useCallback(async (checklistId, itemId) => {
    if (!enabled || !userId) return;

    try {
      const response = await api.post(
        `${apiBaseUrl}/progress/checklists/${checklistId}/complete-item?user_id=${userId}&item_id=${itemId}`
      );
      const progress = response.data;
      setChecklistProgress(prev => ({ ...prev, [checklistId]: progress }));
    } catch (err) {
      console.error('Failed to complete checklist item:', err);
    }
  }, [apiBaseUrl, enabled, userId]);

  const dismissChecklist = useCallback((checklistId) => {
    if (activeChecklist?.id === checklistId) {
      setActiveChecklist(null);
    }
  }, [activeChecklist?.id]);

  const getThemeForTour = useCallback((tour) => {
    if (!tour?.theme_id) {
      return themes.find(t => t.name === 'Default WMS Theme') || themes[0];
    }
    return themes.find(t => t.id === tour.theme_id) || themes[0];
  }, [themes]);

  useEffect(() => {
    if (enabled) {
      setIsLoading(true);
      Promise.all([
        fetchTours(),
        fetchChecklists(),
        fetchLaunchers(),
        fetchThemes(),
      ]).finally(() => {
        setIsLoading(false);
      });
    }
  }, [enabled, fetchTours, fetchChecklists, fetchLaunchers, fetchThemes]);

  useEffect(() => {
    if (enabled && currentPage) {
      fetchTours();
      fetchLaunchers();
    }
  }, [currentPage, enabled, fetchTours, fetchLaunchers]);

  useEffect(() => {
    if (!enabled || !tours.length) return;

    const autoTriggerTours = tours.filter(
      tour => tour.trigger_type === 'auto' && 
              tour.target_pages?.includes(currentPage) &&
              !tourProgress[tour.id]?.completed_at &&
              !tourProgress[tour.id]?.dismissed_at
    );

    if (autoTriggerTours.length > 0 && !activeTour) {
      startTour(autoTriggerTours[0].id);
    }
  }, [tours, currentPage, tourProgress, activeTour, enabled, startTour]);

  const contextValue = {
    tours,
    checklists,
    launchers,
    themes,
    activeTour,
    activeChecklist,
    tourProgress,
    checklistProgress,
    isLoading,
    error,
    startTour,
    advanceTourStep,
    dismissTour,
    completeChecklistItem,
    dismissChecklist,
    getThemeForTour,
    fetchTours,
    fetchChecklists,
    fetchLaunchers,
    fetchTourProgress,
  };

  const currentTheme = activeTour ? getThemeForTour(activeTour) : themes[0];
  const currentChecklistProgress = activeChecklist 
    ? checklistProgress[activeChecklist.id]?.completed_items || []
    : [];

  return (
    <OnboardingContext.Provider value={contextValue}>
      {children}

      {activeTour && (
        <OnboardingTour
          tour={activeTour}
          isOpen={Boolean(activeTour)}
          onClose={(reason) => dismissTour(activeTour.id, reason)}
          onStepComplete={(stepId) => advanceTourStep(activeTour.id, stepId)}
          onTourComplete={() => dismissTour(activeTour.id, 'completed')}
          theme={currentTheme}
          initialStepIndex={tourProgress[activeTour.id]?.current_step_index || 0}
        />
      )}

      {activeChecklist && (
        <OnboardingChecklist
          checklist={activeChecklist}
          completedItems={currentChecklistProgress}
          isVisible={Boolean(activeChecklist) && !activeTour}
          onItemComplete={(itemId) => completeChecklistItem(activeChecklist.id, itemId)}
          onDismiss={() => dismissChecklist(activeChecklist.id)}
          onStartTour={startTour}
          theme={currentTheme}
        />
      )}

      {launchers.map(launcher => (
        <OnboardingLauncher
          key={launcher.id}
          launcher={launcher}
          onStartTour={startTour}
          theme={currentTheme}
        />
      ))}
    </OnboardingContext.Provider>
  );
};

OnboardingProvider.propTypes = {
  children: PropTypes.node.isRequired,
  userId: PropTypes.string,
  userRole: PropTypes.string,
  currentPage: PropTypes.string,
  apiBaseUrl: PropTypes.string,
  enabled: PropTypes.bool,
};

export default React.memo(OnboardingProvider);
